<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import TabBarItem from '@/components/TabBarItem.vue'
import { useAuth } from '@/composables/useAuth'
import { useCompetition } from '@/composables/useCompetition'
import { tabs } from '@/config/tabs'

const route = useRoute()
const { user } = useAuth()
const { currentCompetition } = useCompetition()

/**
 * Le nom d'une compétition réduit à son premier mot (« Splatoon tournois » →
 * « Splatoon »), la place sous l'icône étant comptée. Exception : un premier
 * mot de 3 caractères ou moins (« LFL Cup », « Cup » seul ne dirait rien) — on
 * garde alors le nom entier, quitte à le laisser tronquer par l'ellipsis.
 */
function shortCompetitionName(name: string): string {
  const [first] = name.trim().split(/\s+/)
  return first && first.length > 3 ? first : name
}

/**
 * Le libellé d'un onglet : celui de la conf, sauf « Profil » qui prend le
 * username (sans discriminant) une fois connecté, et « Compétition » qui prend
 * le nom de la compétition sélectionnée (réduit à son premier mot, cf.
 * `shortCompetitionName`) — dans les deux cas le libellé est en
 * `text-overflow: ellipsis`, un nom long est simplement tronqué. Pour
 * « Compétition », on retombe sur le libellé de la conf tant que le catalogue
 * est en vol, en erreur, ou sans sélection.
 */
function labelFor(tab: (typeof tabs)[number]): string {
  if (tab.name === 'profile' && user.value?.shortName) return user.value.shortName
  if (tab.name === 'competition' && currentCompetition.value?.name)
    return shortCompetitionName(currentCompetition.value.name)
  return tab.label
}

/**
 * Index de l'onglet courant, -1 si la route affichée n'en est pas un (la bulle
 * disparaît alors plutôt que de se coller au premier onglet).
 *
 * L'onglet est `meta.tab` quand la route en déclare un, sinon `route.name` : une
 * **sous-page** (classement, détail de match) n'est pas un onglet
 * mais appartient à l'un d'eux, et doit en garder la bulle allumée.
 * `route.name` peut valoir `symbol | undefined` : on ne compare que des string.
 */
const activeIndex = computed(() => {
  const name =
    route.meta.tab ?? (typeof route.name === 'string' ? route.name : '')
  return tabs.findIndex((tab) => tab.name === name)
})
</script>

<template>
  <nav class="tab-bar" aria-label="Navigation principale">
    <div class="tab-bar__surface material">
      <!--
        Bulle glissante, sous les items. Sa géométrie reproduit le layout de la
        surface : largeur = (largeur de la boîte de padding - le `--tabbar-inset`
        des deux côtés - les gaps de 2px entre items) / nombre d'onglets, et le
        déplacement d'un cran vaut sa propre largeur plus un gap. Le padding
        passe par le token plutôt que par une valeur en dur : il doit rester
        celui de `.tab-bar__surface`, dont dépend aussi le rayon concentrique.
      -->
      <span
        v-if="activeIndex >= 0"
        class="tab-bar__bubble"
        aria-hidden="true"
        :style="{
          width: `calc((100% - 2 * var(--tabbar-inset) - ${(tabs.length - 1) * 2}px) / ${tabs.length})`,
          transform: `translateX(calc(${activeIndex} * (100% + 2px)))`,
        }"
      />

      <TabBarItem
        v-for="tab in tabs"
        :key="tab.name"
        :to="tab.name"
        :label="labelFor(tab)"
        :icon="tab.icon"
      />
    </div>
  </nav>
</template>

<style scoped>
.tab-bar {
  /* `absolute`, pas `fixed` : en PWA installée sur iOS 26, un bug WebKit
     (301108) raccourcit le viewport « fixed » de la hauteur de l'encoche,
     ce qui fait flotter une barre fixed trop haut. `.app-shell`, son parent
     direct, a `position: relative` et occupe exactement la taille de
     l'écran (cf. commentaire `100lvh` dans App.vue) : ancrée en absolute par
     rapport à lui, la barre n'a plus à dépendre du viewport fixed. */
  position: absolute;
  z-index: 100;

  /* Barre DÉTACHÉE du bord : marge latérale + marge basse, chacune un `max()`
     entre la gouttière et la safe area correspondante (iPhone sans bouton
     home) — pas une addition, sinon la barre flotte trop haut. */
  left: max(var(--tabbar-gutter), var(--safe-left));
  right: max(var(--tabbar-gutter), var(--safe-right));
  bottom: var(--tabbar-bottom);

  /* la barre ne doit pas intercepter les taps en dehors de la pilule */
  pointer-events: none;
}

.tab-bar__surface {
  pointer-events: auto;

  /* `relative` : c'est le repère de la bulle en position absolue */
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 2px;

  height: var(--tabbar-height);
  padding: var(--tabbar-inset);
  margin: 0 auto;
  /* sur grand écran / tablette on évite une barre démesurément large */
  max-width: 480px;

  border-radius: var(--tabbar-radius);

  /* fond, flou et bordure viennent de `.material` (global.css) ; on cumule le
     reflet du verre avec l'ombre portée de la barre flottante */
  box-shadow: var(--material-highlight), var(--shadow-floating);
}

.tab-bar__bubble {
  position: absolute;
  top: var(--tabbar-inset);
  bottom: var(--tabbar-inset);
  left: var(--tabbar-inset);

  /* Rayon CONCENTRIQUE : rayon de la barre moins l'épaisseur qui l'en sépare.
     La barre étant une pilule (rayon = hauteur / 2), la bulle en est une aussi
     et s'emboîte exactement — un rayon en dur les désaccorderait. */
  border-radius: calc(var(--tabbar-radius) - var(--tabbar-inset));
  background-color: var(--color-accent-soft);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25);

  transition: transform 0.35s var(--ease-ios);
}
</style>
